<script setup lang="ts">
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import { useLocale } from '@/plugins/translation';
import { useDisplay } from 'vuetify';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps<{
  area: Area;
  subject: Category;
}>();

const emit = defineEmits<{
  (e: 'films-clicked', area: Area): void;
}>();

const { t, l } = useLocale();

const onFilmsClick = () => {
  emit('films-clicked', props.area);
};

const { xs } = useDisplay();
</script>

<template>
  <v-hover v-slot="areaBtn">
    <v-card :height="70" v-bind="areaBtn.props" class="bg-subject area-button text-center px-2">
      <v-card-item class="pa-0">
        <v-card-title class="area-button__title" :class="{ 'area-button__title--expanded mb-8': areaBtn.isHovering }">
          {{ area.name }}
        </v-card-title>
      </v-card-item>
      <v-sheet location="end bottom" :height="areaBtn.isHovering ? 32 : 0" class="bg-white area-button__menu">
        <v-hover v-slot="filmBtn">
          <v-btn
            slim
            :style="`width: ${area.guide?.active ? '50%' : '100%'}; height: 100%`"
            v-bind="filmBtn.props"
            variant="text"
            size="small"
            class="text-none nav-button"
            @click="onFilmsClick"
            :aria-label="t(l.tabs.go_to_films_in_area, { area: area.name })"
            >{{ t(l.tabs.films) }}
            <template #prepend>
              <FontAwesomeIcon
                :icon="`fa-video ${filmBtn.isHovering ? 'fa-solid' : 'fa-regular'}`"
                :size="xs ? 'sm' : 'lg'"
              ></FontAwesomeIcon>
            </template>
          </v-btn>
        </v-hover>
        <v-hover v-slot="guideBtn">
          <v-btn
            v-if="area.guide?.active"
            v-bind="guideBtn.props"
            slim
            style="width: 50%; height: 100%"
            variant="text"
            size="small"
            class="text-none nav-button"
            :to="`/videos/${subject.slug}/${area.slug}`"
            :aria-label="t(l.area_guide.go_to_area_guide, { area: area.name })"
            >{{ t(l.area_guide.go_to) }}
            <template #prepend>
              <FontAwesomeIcon
                size="lg"
                :icon="guideBtn.isHovering ? 'fa-solid fa-rocket-launch' : 'fa-regular fa-rocket-launch'"
              ></FontAwesomeIcon>
            </template>
          </v-btn>
        </v-hover>
      </v-sheet>
    </v-card>
  </v-hover>
</template>

<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.area-button {
  container-type: size;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: center;
  transition: all 0.2s ease-in-out;
}
.area-button__title {
  display: inline-block;
  font: 500 1.125rem/1.15 'Filson-Soft';
  max-width: 100cqw;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
  transition: all 0.2s ease-in-out;

  &--expanded {
    -webkit-line-clamp: 1;
    font-size: 1rem;
    transition: all 0.2s ease-in-out;

    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      font-size: 0.8rem;
    }
  }
}

.area-button__menu {
  display: flex;
  transition: all 0.2s ease-in-out;
  width: 100%;
  position: absolute;
  bottom: 0;
  height: 50%;

  .nav-button {
    font-size: 1rem;
    letter-spacing: normal;
    color: rgb(var(--v-theme-primary)) !important;

    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      font-size: 0.75rem;
    }
  }
}
</style>
